/**
 * Standard effects — built-in effects with default implementations.
 *
 * These effects are always available without requiring explicit host handlers.
 * Host handlers can override them (host handlers take priority in the lookup order).
 *
 * Lookup order: local try/with → host handlers → standard effects → unhandled error
 *
 * Each standard effect has co-located documentation (FunctionDocs) and arity,
 * mirroring the structure of builtin normal expressions and module functions.
 *
 * Sync effects work in both `runSync` and `run`.
 * Async effects (`dvala.sleep`, `dvala.io.read` in Node, `dvala.io.readStdin`) only work in `run` —
 * `runSync` will throw when a Promise surfaces.
 */

import { ArithmeticError, RuntimeError, TypeError } from '../errors'
import type { Arity, FunctionDocs } from '../builtin/interface'
import type { Any } from '../interface'
import { isEffect, isRegularExpression } from '../typeGuards/dvala'
import { isDvalaFunction } from '../typeGuards/dvalaFunction'
import { toFixedArity } from '../utils/arity'
import { isPersistentMap, isPersistentVector, PersistentVector } from '../utils/persistent'
import type { SourceCodeInfo } from '../tokenizer/token'
import type { ContinuationStack } from './frames'
import type { Step } from './step'

// ---------------------------------------------------------------------------
// Standard effect definition type
// ---------------------------------------------------------------------------

/**
 * A standard effect handler returns the next step directly.
 * Sync effects return `Step`, async effects return `Promise<Step>`.
 */
type StandardEffectHandlerFn = (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo) => Step | Promise<Step>

/**
 * A standard effect definition — mirrors BuiltinNormalExpression structure.
 * Each effect has a handler function, arity for validation, and co-located docs.
 */
export interface StandardEffectDefinition {
  handler?: StandardEffectHandlerFn
  arity: Arity
  docs: FunctionDocs
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function isNode(): boolean {
  return typeof process !== 'undefined' && typeof process.stdout?.write === 'function'
}

/**
 * Format a Dvala value for text output (stdout/stderr).
 * - Strings are printed as-is (no quotes).
 * - Numbers, booleans, null: String() coercion.
 * - Functions: `<function name>` or `<builtin function name>`.
 * - Effects: `<effect name>`.
 * - RegExps (Dvala regular expressions): `/pattern/flags`.
 * - Arrays and objects: JSON.stringify with 2-space indent.
 */
function formatForOutput(value: unknown): string {
  if (typeof value === 'string')
    return value

  if (value === null)
    return 'null'

  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value)

  if (isDvalaFunction(value)) {
    if (value.functionType === 'Builtin')
      return `<builtin function ${value.normalBuiltinSymbolType}>`
    const kind = value.functionType === 'Macro' ? 'macro' : 'function'
    return `<${kind} ${'name' in value && value.name ? value.name : '\u03BB'}>`
  }

  if (isEffect(value))
    return `<effect ${value.name}>`

  if (isRegularExpression(value))
    return `/${value.s}/${value.f}`

  if (typeof value === 'object' && value instanceof RegExp)
    return `${value}`

  // Arrays and objects — JSON.stringify with special value handling
  return JSON.stringify(replaceSpecialValues(value), null, 2)
}

function replaceSpecialValues(value: unknown): unknown {
  if (isDvalaFunction(value))
    return formatForOutput(value)
  if (isEffect(value))
    return formatForOutput(value)
  if (isRegularExpression(value))
    return formatForOutput(value)
  if (Array.isArray(value))
    return value.map(replaceSpecialValues)
  if (typeof value === 'object' && value !== null) {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value))
      result[key] = replaceSpecialValues(val)
    return result
  }
  return value
}

function printHandler(arg: Any, k: ContinuationStack): Step {
  const value = arg
  const str = formatForOutput(value)
  if (isNode()) {
    process.stdout.write(str)
  } else {
    // eslint-disable-next-line no-console
    console.log(str)
  }
  return { type: 'Value', value, k }
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.random() * 16 | 0
    const value = char === 'x' ? random : (random & 0x3 | 0x8)
    return value.toString(16)
  })
}

// ---------------------------------------------------------------------------
// Standard effect name union — add new effects here
// ---------------------------------------------------------------------------

type StandardEffectName =
  | 'dvala.io.print'
  | 'dvala.io.error'
  | 'dvala.io.read'
  | 'dvala.io.pick'
  | 'dvala.io.confirm'
  | 'dvala.io.readStdin'
  | 'dvala.random'
  | 'dvala.random.uuid'
  | 'dvala.random.int'
  | 'dvala.random.item'
  | 'dvala.random.shuffle'
  | 'dvala.time.now'
  | 'dvala.time.zone'
  | 'dvala.checkpoint'
  | 'dvala.sleep'
  | 'dvala.host'
  | 'dvala.env'
  | 'dvala.args'

// ---------------------------------------------------------------------------
// Standard effect definitions (handler + arity + docs)
// ---------------------------------------------------------------------------

const standardEffects: Record<StandardEffectName, StandardEffectDefinition> = {

  // ── I/O ──────────────────────────────────────────────────────────────────

  'dvala.io.print': {
    handler: printHandler,
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Writes a value to stdout without a trailing newline. Accepts any value — strings are printed as-is, other values are auto-formatted. In Node.js uses `process.stdout.write(str)`, in browsers uses `console.log(str)`. Resumes with the original value (identity).',
      returns: { type: 'any' },
      args: {
        value: { type: 'any', description: 'Value to print.' },
      },
      variants: [{ argumentNames: ['value'] }],
      examples: [
        { code: 'perform(@dvala.io.print, "hello")', noRun: true },
        { code: 'perform(@dvala.io.print, 42)', noRun: true },
        { code: 'perform(@dvala.io.print, {name: "Alice", age: 30})', noRun: true },
      ],
      seeAlso: ['-effect-dvala.io.error', '-effect-dvala.io.read', 'perform', 'effect'],
    },
  },

  'dvala.io.error': {
    handler: (arg: Any, k: ContinuationStack): Step => {
      const str = formatForOutput(arg)
      if (isNode()) {
        process.stderr.write(`${str}\n`)
      } else {
        // eslint-disable-next-line no-console
        console.error(str)
      }
      return { type: 'Value', value: arg, k }
    },
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Writes a value to stderr followed by a newline. Accepts any value — strings are printed as-is, other values are auto-formatted. In Node.js uses `process.stderr.write(str + "\\n")`, in browsers uses `console.error(str)`. Resumes with the original value (identity).',
      returns: { type: 'any' },
      args: {
        value: { type: 'any', description: 'Value to write to stderr.' },
      },
      variants: [{ argumentNames: ['value'] }],
      examples: [
        { code: 'perform(@dvala.io.error, "something went wrong")', noRun: true },
        { code: 'perform(@dvala.io.error, {code: 500, message: "Internal error"})', noRun: true },
      ],
      seeAlso: ['-effect-dvala.io.print', 'perform', 'effect'],
    },
  },

  'dvala.io.read': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      const message = typeof arg === 'string' ? arg : ''

      // Browser: window.prompt (synchronous)
      if (typeof globalThis.prompt === 'function') {

        const result = globalThis.prompt(message)
        return { type: 'Value', value: result ?? null, k }
      }

      throw new RuntimeError('dvala.io.read is not supported in this environment. In Node.js, register a "dvala.io.read" host handler.', sourceCodeInfo)
    },
    arity: { min: 0, max: 1 },
    docs: {
      category: 'effect',
      description: 'Reads one line of user input. In browsers uses `window.prompt()`. In Node.js uses `readline`. Resumes with the user\'s input string, or `null` on cancel.',
      returns: { type: ['string', 'null'] },
      args: {
        message: { type: 'string', description: 'Optional prompt message to display.' },
      },
      variants: [
        { argumentNames: [] },
        { argumentNames: ['message'] },
      ],
      examples: [
        { code: 'let name = perform(@dvala.io.read, "What is your name?"); "Hello, " ++ name', noRun: true },
        { code: 'let input = perform(@dvala.io.read); input ?? "no input"', noRun: true },
      ],
      seeAlso: ['-effect-dvala.io.readStdin', '-effect-dvala.io.print', '-effect-dvala.io.pick', '-effect-dvala.io.confirm', 'perform', 'effect'],
    },
  },

  'dvala.io.pick': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      // arg is either a PersistentVector (array of items) or a PersistentMap (object with items/options)
      const items = isPersistentVector(arg) ? arg : isPersistentMap(arg) ? arg.get('items') : undefined
      const options = isPersistentVector(arg) ? undefined : isPersistentMap(arg) ? arg.get('options') : undefined

      if (!isPersistentVector(items)) {
        throw new TypeError(`dvala.io.pick: first argument must be an array, got ${typeof items}`, sourceCodeInfo)
      }
      if (items.size === 0) {
        throw new TypeError('dvala.io.pick: items array must not be empty', sourceCodeInfo)
      }
      for (let i = 0; i < items.size; i++) {
        if (typeof items.get(i) !== 'string') {
          throw new TypeError(`dvala.io.pick: items[${i}] must be a string, got ${typeof items.get(i)}`, sourceCodeInfo)
        }
      }

      let promptMessage: string | undefined
      let defaultIndex: number | undefined

      if (options !== undefined) {
        if (!isPersistentMap(options)) {
          throw new TypeError(`dvala.io.pick: second argument must be an object, got ${typeof options}`, sourceCodeInfo)
        }
        if (options.get('prompt') !== undefined) {
          if (typeof options.get('prompt') !== 'string') {
            throw new TypeError('dvala.io.pick: options.prompt must be a string', sourceCodeInfo)
          }
          promptMessage = options.get('prompt') as string
        }
        if (options.get('default') !== undefined) {
          const optDefault = options.get('default')
          if (typeof optDefault !== 'number' || !Number.isInteger(optDefault)) {
            throw new TypeError('dvala.io.pick: options.default must be an integer', sourceCodeInfo)
          }
          defaultIndex = optDefault
          if (defaultIndex < 0 || defaultIndex >= items.size) {
            throw new TypeError(`dvala.io.pick: options.default (${defaultIndex}) is out of bounds for array of length ${items.size}`, sourceCodeInfo)
          }
        }
      }

      // Browser: window.prompt (synchronous)
      if (typeof globalThis.prompt === 'function') {
        const listLines = [...items].map((item, i) => `${i}: ${item}`).join('\n')
        const header = promptMessage ?? 'Choose an item:'
        const defaultHint = defaultIndex !== undefined ? ` [default: ${defaultIndex}]` : ''
        const message = `${header}${defaultHint}\n${listLines}`

        const result = globalThis.prompt(message)
        if (result === null) {
          return { type: 'Value', value: null, k }
        }
        const trimmed = result.trim()
        if (trimmed === '') {
          return { type: 'Value', value: defaultIndex !== undefined ? defaultIndex : null, k }
        }
        const parsed = Number(trimmed)
        if (!Number.isInteger(parsed) || parsed < 0 || parsed >= items.size) {
          throw new TypeError(`dvala.io.pick: invalid selection "${trimmed}"`, sourceCodeInfo)
        }
        return { type: 'Value', value: parsed, k }
      }

      throw new RuntimeError('dvala.io.pick is not supported in this environment. In Node.js, register a "dvala.io.pick" host handler.', sourceCodeInfo)
    },
    arity: { min: 1, max: 2 },
    docs: {
      category: 'effect',
      description: 'Presents a list of items and asks the user to choose one. Accepts either a plain array of strings or an object with `items` and optional `options`. Resumes with the index of the chosen item, or `null` if the user cancels.',
      returns: { type: ['integer', 'null'] },
      args: {
        items: { type: 'array', description: 'Non-empty array of strings to display. Can be passed directly as the arg.' },
        options: { type: 'object', description: 'Optional settings: `prompt` (string label) and `default` (integer index to use when the user submits an empty input).' },
      },
      variants: [
        { argumentNames: ['items'] },
        { argumentNames: ['items', 'options'] },
      ],
      examples: [
        { code: 'let choice = perform(@dvala.io.pick, ["Red", "Green", "Blue"])', noRun: true },
        { code: 'let choice = perform(@dvala.io.pick, {items: ["Red", "Green", "Blue"], options: {prompt: "Pick a color:", default: 0}})', noRun: true },
        { code: 'do\n  let colors = ["Red", "Green", "Blue"];\n  let idx = perform(@dvala.io.pick, colors);\n  if idx != null then nth(colors, idx) else "No selection" end\nend', noRun: true },
      ],
      seeAlso: ['-effect-dvala.io.read', '-effect-dvala.io.confirm', 'perform', 'effect'],
    },
  },

  'dvala.io.confirm': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      // arg is either a string question or a PersistentMap with question/options fields
      const question = typeof arg === 'string' ? arg : isPersistentMap(arg) ? arg.get('question') : undefined
      const options = typeof arg === 'string' ? undefined : isPersistentMap(arg) ? arg.get('options') : undefined

      if (typeof question !== 'string') {
        throw new TypeError(`dvala.io.confirm: first argument must be a string, got ${typeof question}`, sourceCodeInfo)
      }

      if (options !== undefined) {
        if (!isPersistentMap(options)) {
          throw new TypeError(`dvala.io.confirm: second argument must be an object, got ${typeof options}`, sourceCodeInfo)
        }
        if (options.get('default') !== undefined && typeof options.get('default') !== 'boolean') {
          throw new TypeError('dvala.io.confirm: options.default must be a boolean', sourceCodeInfo)
        }
      }

      // Browser: window.confirm (synchronous)
      if (typeof globalThis.confirm === 'function') {
        return { type: 'Value', value: globalThis.confirm(question), k }
      }

      throw new RuntimeError('dvala.io.confirm is not supported in this environment. In Node.js, register a "dvala.io.confirm" host handler.', sourceCodeInfo)
    },
    arity: { min: 1, max: 2 },
    docs: {
      category: 'effect',
      description: 'Asks the user a yes/no question. In browsers uses `window.confirm()` and returns `true` (OK) or `false` (Cancel). In Node.js, register a host handler. The optional `default` hints the preferred answer to host handlers (e.g. for rendering `[Y/n]` in a CLI), but has no effect on the browser implementation.',
      returns: { type: 'boolean' },
      args: {
        question: { type: 'string', description: 'The yes/no question to present.' },
        options: { type: 'object', description: 'Optional settings: `default` (boolean, hints the preferred answer for host handlers).' },
      },
      variants: [
        { argumentNames: ['question'] },
        { argumentNames: ['question', 'options'] },
      ],
      examples: [
        { code: 'if perform(@dvala.io.confirm, "Delete all files?") then "Deleted" else "Cancelled" end', noRun: true },
        { code: 'perform(@dvala.io.confirm, {question: "Continue?", options: {default: true}})', noRun: true },
      ],
      seeAlso: ['-effect-dvala.io.read', '-effect-dvala.io.pick', 'perform', 'effect'],
    },
  },

  'dvala.io.readStdin': {
    handler: (_arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Promise<Step> => {
      if (!isNode() || !process.stdin) {
        throw new RuntimeError('dvala.io.readStdin is not supported in this environment. Node.js is required.', sourceCodeInfo)
      }
      return new Promise<Step>((resolve, reject) => {
        const chunks: string[] = []
        process.stdin.setEncoding('utf-8')
        process.stdin.on('data', (chunk: string) => chunks.push(chunk))
        process.stdin.on('end', () => resolve({ type: 'Value', value: chunks.join(''), k }))
        process.stdin.on('error', (err: Error) => reject(err))
        process.stdin.resume()
      })
    },
    arity: toFixedArity(0),
    docs: {
      category: 'effect',
      description: 'Reads all of stdin until EOF (Node.js only). Resumes with the full stdin content as a string.',
      returns: { type: 'string' },
      args: {},
      variants: [{ argumentNames: [] }],
      examples: [
        { code: 'let input = perform(@dvala.io.readStdin); count(input)', noRun: true },
      ],
      seeAlso: ['-effect-dvala.io.read', 'perform', 'effect'],
    },
  },

  // ── Random ───────────────────────────────────────────────────────────────

  'dvala.random': {
    handler: (_arg: Any, k: ContinuationStack): Step => {
      return { type: 'Value', value: Math.random(), k }
    },
    arity: toFixedArity(0),
    docs: {
      category: 'effect',
      description: 'Returns a random floating-point number in the range [0, 1). Equivalent to `Math.random()` in JavaScript.',
      returns: { type: 'number' },
      args: {},
      variants: [{ argumentNames: [] }],
      examples: [
        'perform(@dvala.random)',
      ],
      seeAlso: ['-effect-dvala.random.int', '-effect-dvala.random.uuid', '-effect-dvala.random.item', '-effect-dvala.random.shuffle', 'perform', 'effect'],
    },
  },

  'dvala.random.uuid': {
    handler: (_arg: Any, k: ContinuationStack): Step => {
      return { type: 'Value', value: generateUUID(), k }
    },
    arity: toFixedArity(0),
    docs: {
      category: 'effect',
      description: 'Generates a UUID v4 string.',
      returns: { type: 'string' },
      args: {},
      variants: [{ argumentNames: [] }],
      examples: [
        'perform(@dvala.random.uuid)',
      ],
      seeAlso: ['-effect-dvala.random', 'perform', 'effect'],
    },
  },

  'dvala.random.int': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      // arg is a PersistentVector [min, max] — access via .get()
      const min = isPersistentVector(arg) ? arg.get(0) : (Array.isArray(arg) ? arg[0] : undefined)
      const max = isPersistentVector(arg) ? arg.get(1) : (Array.isArray(arg) ? arg[1] : undefined)
      if (typeof min !== 'number' || !Number.isInteger(min)) {
        throw new TypeError(`dvala.random.int: min must be an integer, got ${typeof min === 'number' ? min : typeof min}`, sourceCodeInfo)
      }
      if (typeof max !== 'number' || !Number.isInteger(max)) {
        throw new TypeError(`dvala.random.int: max must be an integer, got ${typeof max === 'number' ? max : typeof max}`, sourceCodeInfo)
      }
      if (max <= min) {
        throw new ArithmeticError(`dvala.random.int: max (${max}) must be greater than min (${min})`, sourceCodeInfo)
      }
      return { type: 'Value', value: Math.floor(Math.random() * (max - min)) + min, k }
    },
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Returns a random integer in the range [min, max). Pass a two-element array `[min, max]`.',
      returns: { type: 'integer' },
      args: {
        range: { type: 'array', description: 'A two-element array [min, max] where min is inclusive and max is exclusive. Both must be integers with max > min.' },
      },
      variants: [{ argumentNames: ['range'] }],
      examples: [
        'perform(@dvala.random.int, [1, 100])',
      ],
      seeAlso: ['-effect-dvala.random', '-effect-dvala.random.item', 'perform', 'effect'],
    },
  },

  'dvala.random.item': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      // arg is a PersistentVector — accept PV or plain array
      if (!isPersistentVector(arg) && !Array.isArray(arg)) {
        throw new TypeError(`dvala.random.item: argument must be an array, got ${typeof arg}`, sourceCodeInfo)
      }
      const size = isPersistentVector(arg) ? arg.size : (arg as unknown[]).length
      if (size === 0) {
        throw new TypeError('dvala.random.item: cannot pick from an empty array', sourceCodeInfo)
      }
      const index = Math.floor(Math.random() * size)
      const value = isPersistentVector(arg) ? arg.get(index) as Any : (arg as Any[])[index] as Any
      return { type: 'Value', value, k }
    },
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Picks a random element from a non-empty array.',
      returns: { type: 'any' },
      args: {
        array: { type: 'array', description: 'Non-empty array to pick from.' },
      },
      variants: [{ argumentNames: ['array'] }],
      examples: [
        'perform(@dvala.random.item, ["a", "b", "c"])',
      ],
      seeAlso: ['-effect-dvala.random', '-effect-dvala.random.shuffle', '-effect-dvala.random.int', 'perform', 'effect'],
    },
  },

  'dvala.random.shuffle': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      if (!isPersistentVector(arg)) {
        throw new TypeError(`dvala.random.shuffle: argument must be an array, got ${typeof arg}`, sourceCodeInfo)
      }
      // Copy elements into a mutable array, perform Fisher-Yates in place, then wrap back in PersistentVector
      const shuffled: Any[] = [...arg] as Any[]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const tmp = shuffled[i]
        shuffled[i] = shuffled[j]!
        shuffled[j] = tmp!
      }
      return { type: 'Value', value: PersistentVector.from(shuffled), k }
    },
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Returns a new array with the elements of the input array in random order. Uses the Fisher-Yates shuffle algorithm.',
      returns: { type: 'array' },
      args: {
        array: { type: 'array', description: 'Array to shuffle.' },
      },
      variants: [{ argumentNames: ['array'] }],
      examples: [
        'perform(@dvala.random.shuffle, [1, 2, 3, 4, 5])',
      ],
      seeAlso: ['-effect-dvala.random', '-effect-dvala.random.item', 'perform', 'effect'],
    },
  },

  // ── Time ─────────────────────────────────────────────────────────────────

  'dvala.time.now': {
    handler: (_arg: Any, k: ContinuationStack): Step => {
      return { type: 'Value', value: Date.now(), k }
    },
    arity: toFixedArity(0),
    docs: {
      category: 'effect',
      description: 'Returns the current timestamp in milliseconds since the Unix epoch. Equivalent to `Date.now()` in JavaScript.',
      returns: { type: 'number' },
      args: {},
      variants: [{ argumentNames: [] }],
      examples: [
        'perform(@dvala.time.now)',
      ],
      seeAlso: ['-effect-dvala.time.zone', '-effect-dvala.sleep', 'perform', 'effect'],
    },
  },

  'dvala.time.zone': {
    handler: (_arg: Any, k: ContinuationStack): Step => {
      return { type: 'Value', value: Intl.DateTimeFormat().resolvedOptions().timeZone, k }
    },
    arity: toFixedArity(0),
    docs: {
      category: 'effect',
      description: 'Returns the current IANA timezone string, e.g. `"Europe/Stockholm"` or `"America/New_York"`.',
      returns: { type: 'string' },
      args: {},
      variants: [{ argumentNames: [] }],
      examples: [
        'perform(@dvala.time.zone)',
      ],
      seeAlso: ['-effect-dvala.time.now', 'perform', 'effect'],
    },
  },

  // ── Snapshot ──────────────────────────────────────────────────────────────

  'dvala.checkpoint': {
    // The actual snapshot capture is handled as a special case in dispatchPerform
    // (unconditional capture before normal dispatch). No handler here — checkpoint
    // propagates through the normal handler chain (local, host, wildcard).
    // If completely unhandled, dispatchPerform resolves it to null.
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Captures a snapshot of the current program state (continuation stack). The snapshot is stored in an in-memory list accessible via `ctx.snapshots` in host handlers. Takes a mandatory message string. The standard fallback resumes with `null`, but host handlers can override the resume value. The snapshot is always captured regardless of whether a handler intercepts.',
      returns: { type: 'null' },
      args: {
        message: { type: 'string', description: 'A human-readable label for the checkpoint.' },
      },
      variants: [
        { argumentNames: ['message'] },
      ],
      examples: [
        'perform(@dvala.checkpoint, "init")',
        'perform(@dvala.checkpoint, "analysis-done")',
      ],
      seeAlso: ['perform', 'effect'],
    },
  },

  // ── Async ────────────────────────────────────────────────────────────────

  'dvala.sleep': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Promise<Step> => {
      const ms = arg
      if (typeof ms !== 'number' || ms < 0) {
        throw new TypeError(`dvala.sleep requires a non-negative number argument, got ${typeof ms === 'number' ? ms : typeof ms}`, sourceCodeInfo)
      }
      return new Promise<Step>(resolve => {
        setTimeout(() => resolve({ type: 'Value', value: null, k }), ms)
      })
    },
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Waits for the specified number of milliseconds before resuming. Resumes with `null`. Only works in async execution (`run`) — `runSync` will throw when a Promise surfaces.',
      returns: { type: 'null' },
      args: {
        ms: { type: 'number', description: 'The number of milliseconds to sleep. Must be a non-negative number.' },
      },
      variants: [{ argumentNames: ['ms'] }],
      examples: [
        { code: 'do perform(@dvala.sleep, 1000); "awake!" end', noRun: true },
        { code: 'do perform(@dvala.io.print, "waiting..."); perform(@dvala.sleep, 500); perform(@dvala.io.print, "done!") end', noRun: true },
      ],
      seeAlso: ['-effect-dvala.time.now', 'perform', 'effect'],
    },
  },

  // ── Host interaction ────────────────────────────────────────────────────

  'dvala.host': {
    // No default handler — @dvala.host is dispatched to host-registered handlers only.
    // If unhandled, dispatchPerform produces a custom error message (not the generic
    // "Unhandled effect" message). This definition exists for docs and arity validation.
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Requests a host-injected value by name. The host must install an effect handler for `@dvala.host` that resumes with the value. If no handler is installed, a descriptive error is thrown. Use this to receive configuration and bindings from the host environment.',
      returns: { type: 'any' },
      args: {
        name: { type: 'string', description: 'The name of the host binding to retrieve.' },
      },
      variants: [{ argumentNames: ['name'] }],
      examples: [
        { code: 'let configExists = perform(@dvala.host, "configExists")', noRun: true },
      ],
      seeAlso: ['-effect-dvala.env', '-effect-dvala.args', 'perform', 'effect'],
    },
  },

  'dvala.env': {
    handler: (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo): Step => {
      if (typeof arg !== 'string') {
        throw new TypeError(`@dvala.env requires a string argument, got ${typeof arg}`, sourceCodeInfo)
      }
      // Guard for browser: process.env doesn't exist outside Node
      if (typeof process === 'undefined' || !process.env) {
        return { type: 'Value', value: null, k }
      }
      const value = process.env[arg]
      return { type: 'Value', value: value ?? null, k }
    },
    arity: toFixedArity(1),
    docs: {
      category: 'effect',
      description: 'Reads an environment variable by name. Returns the value as a string, or `null` if the variable is not set. Per-entry only — fetch-all is not supported for security and observability.',
      returns: { type: ['string', 'null'] },
      args: {
        name: { type: 'string', description: 'The environment variable name, e.g. `"HOME"` or `"PATH"`.' },
      },
      variants: [{ argumentNames: ['name'] }],
      examples: [
        { code: 'perform(@dvala.env, "HOME")', noRun: true },
        { code: 'let port = perform(@dvala.env, "PORT") ?? "3000"', noRun: true },
      ],
      seeAlso: ['-effect-dvala.host', '-effect-dvala.args', 'perform', 'effect'],
    },
  },

  'dvala.args': {
    handler: (_arg: Any, k: ContinuationStack): Step => {
      // Guard for browser: process.argv doesn't exist outside Node
      if (typeof process === 'undefined' || !process.argv) {
        return { type: 'Value', value: PersistentVector.empty(), k }
      }
      // Strip node binary and script path — return user args only
      const args = process.argv.slice(2)
      return { type: 'Value', value: PersistentVector.from(args), k }
    },
    arity: toFixedArity(0),
    docs: {
      category: 'effect',
      description: 'Returns the command-line arguments as an array of strings. The `node` binary and script path are stripped — only user arguments are included. Returns an empty array in non-CLI contexts (e.g. browser).',
      returns: { type: 'array' },
      args: {},
      variants: [{ argumentNames: [] }],
      examples: [
        { code: 'let args = perform(@dvala.args)', noRun: true },
        { code: 'let [filename, ...flags] = perform(@dvala.args)', noRun: true },
      ],
      seeAlso: ['-effect-dvala.host', '-effect-dvala.env', 'perform', 'effect'],
    },
  },

}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type { StandardEffectName }

/** All standard effect names. */
export const standardEffectNames: ReadonlySet<string> = new Set(Object.keys(standardEffects))

/** All standard effect definitions (for reference data generation). */
export const allStandardEffectDefinitions: Readonly<Record<string, StandardEffectDefinition>> = standardEffects

/**
 * Look up a standard effect definition by name.
 * Returns undefined if the effect is not a standard effect.
 */
export function getStandardEffectDefinition(effectName: string): StandardEffectDefinition | undefined {
  return (standardEffects as Record<string, StandardEffectDefinition>)[effectName]
}

/**
 * Look up a standard effect handler by name.
 * Validates arity before calling the handler.
 * Returns undefined if the effect is not a standard effect.
 */
export function getStandardEffectHandler(effectName: string): ((arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo) => Step | Promise<Step>) | undefined {
  const def = (standardEffects as Record<string, StandardEffectDefinition>)[effectName]
  const handler = def?.handler
  if (!handler)
    return undefined

  return (arg: Any, k: ContinuationStack, sourceCodeInfo?: SourceCodeInfo) => {
    return handler(arg, k, sourceCodeInfo)
  }
}
